import { format, formatDistanceToNowStrict, isThisYear, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import i18n from '@/core/i18n';

/** IANA identifier for UTC, used to render wall-clock timestamps literally. */
const UTC_TIMEZONE = 'UTC';

/**
 * Standard date format used throughout the application (date-fns)
 * Format: "MMM d, yyyy" (e.g., "Jan 15, 2025")
 */
export const STANDARD_DATE_FORMAT = 'MMM d, yyyy';

/**
 * Standard date format for dayjs library
 * Format: "MMM D, YYYY" (e.g., "Jan 15, 2025")
 */
export const STANDARD_DATE_FORMAT_DAYJS = 'MMM D, YYYY';

/**
 * Short date format without year
 * Format: "MMM d" (e.g., "Jan 15")
 */
export const SHORT_DATE_FORMAT = 'MMM d';

/**
 * Date with time format
 * Format: "d MMM yyyy, HH:mm" (e.g., "15 Jan 2025, 14:30")
 */
export const DATE_TIME_FORMAT = 'd MMM yyyy, HH:mm';

/**
 * Weekday date format
 * Format: "EEEE, d MMM" (e.g., "Monday, 1 Jan")
 */
export const WEEKDAY_DATE_FORMAT = 'EEEE, d MMM';

/**
 * Long weekday-with-date format used by time-tracking day detail headers.
 * Format: "EEEE. MMM d, yyyy" (e.g., "Friday. May 8, 2026")
 */
export const LONG_WEEKDAY_DATE_FORMAT = 'EEEE. MMM d, yyyy';

/**
 * Mid-length weekday-with-short-date format used by time-tracking popovers.
 * Format: "EEEE, MMM d" (e.g., "Friday, May 8")
 */
export const WEEKDAY_WITH_SHORT_DATE_FORMAT = 'EEEE, MMM d';

/**
 * Short weekday abbreviation. Format: "EEE" (e.g., "Mon")
 */
export const SHORT_WEEKDAY_FORMAT = 'EEE';

/**
 * Numeric day-of-month. Format: "d" (e.g., "8")
 */
export const DAY_NUMBER_FORMAT = 'd';

/**
 * Full month name. Format: "LLLL" (e.g., "January")
 */
export const MONTH_NAME_FORMAT = 'LLLL';

/**
 * Formats a date to standard display format (e.g., "Jan 1, 2025")
 *
 * @param date - Date object or ISO string
 * @returns Formatted date string
 *
 * @example
 * formatDisplayDate(new Date('2025-01-15')) // "Jan 15, 2025"
 * formatDisplayDate('2025-01-15T00:00:00Z') // "Jan 15, 2025"
 */
export const formatDisplayDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, STANDARD_DATE_FORMAT);
};

/**
 * Formats a wall-clock timestamp to the standard display date (e.g. "Jun 15, 2026)
 * by reading its UTC components literally, WITHOUT converting to the user's timezone.
 *
 * The backend stores worklog timestamps as wall-clock values suffixed with `Z`
 * (e.g. "2026-06-15T11:49:00.000Z"). These must be shown exactly as received —
 * "2026-06-15 11:49" — for every viewer regardless of their browser timezone.
 * Using the regular `formatDisplayDate` would shift the date for non-UTC users.
 *
 * @param date - ISO string or Date carrying a wall-clock value
 * @returns Formatted date string built from the UTC components
 *
 * @example
 * formatWallClockDate('2026-06-15T11:49:00.000Z') // "Jun 15, 2026" for any timezone
 */
export const formatWallClockDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, UTC_TIMEZONE, STANDARD_DATE_FORMAT);
};

/**
 * Formats a wall-clock timestamp's time portion (e.g. "11:49") by reading its
 * UTC components literally, WITHOUT converting to the user's timezone.
 *
 * Mirrors {@link formatWallClockDate} for the `HH:mm` time portion of worklog
 * timestamps — see that helper for why UTC-literal rendering is required.
 *
 * @param date - ISO string or Date carrying a wall-clock value
 * @returns Time string ("HH:mm") built from the UTC components
 *
 * @example
 * formatWallClockTime('2026-06-15T11:49:00.000Z') // "11:49" for any timezone
 */
export const formatWallClockTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, UTC_TIMEZONE, 'HH:mm');
};

/**
 * Formats a wall-clock timestamp to the full date-time display (e.g. "4 May 2026, 00:00")
 * by reading its UTC components literally, WITHOUT converting to the user's timezone.
 *
 * Mirrors {@link formatWallClockDate}/{@link formatWallClockTime} for the combined
 * `DATE_TIME_FORMAT`. Use this (NOT {@link formatDateTime}) for backend worklog timestamps,
 * which are wall-clock values suffixed with `Z` and must be shown exactly as received.
 *
 * @param date - ISO string or Date carrying a wall-clock value
 * @returns Formatted date-time string built from the UTC components
 *
 * @example
 * formatWallClockDateTime('2026-05-04T00:00:00.000Z') // "4 May 2026, 00:00" for any timezone
 */
export const formatWallClockDateTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, UTC_TIMEZONE, DATE_TIME_FORMAT);
};

/**
 * Formats a date range for display
 *
 * @param startDate - Start date (Date object or ISO string)
 * @param endDate - Optional end date (Date object, ISO string, or null for infinite)
 * @returns Formatted date range string
 *
 * @example
 * formatDateRange(new Date('2025-01-01'), new Date('2025-02-28'))
 * // "Jan 1, 2025 - Feb 28, 2025"
 *
 * formatDateRange('2025-01-01', null)
 * // "Jan 1, 2025 - ∞"
 */
export const formatDateRange = (
  startDate: Date | string,
  endDate?: Date | string | null
): string => {
  const start = formatDisplayDate(startDate);

  if (!endDate) {
    return `${start} - ∞`;
  }

  const end = formatDisplayDate(endDate);
  return `${start} - ${end}`;
};

/**
 * Formats a 1-based month index (1–12) to its full month name.
 *
 * @param month - Month index where 1 = January … 12 = December
 * @returns Full month name, or empty string if the index is out of range
 *
 * @example
 * formatMonthName(1)  // "January"
 * formatMonthName(12) // "December"
 */
export const formatMonthName = (month: number): string => {
  if (!Number.isInteger(month) || month < 1 || month > 12) return '';
  // Year/day are arbitrary — only the month component is read by the format.
  return format(new Date(2000, month - 1, 1), MONTH_NAME_FORMAT);
};

/**
 * Formats date to ISO string for API requests
 *
 * @param date - Date object to format
 * @returns ISO 8601 date-time string
 *
 * @example
 * formatDateToISO(new Date('2025-01-15'))
 * // "2025-01-15T00:00:00.000Z"
 */
export const formatDateToISO = (date: Date): string => {
  return date.toISOString();
};

/**
 * Formats date to YYYY-MM-DD format for API requests
 * Uses UTC date to avoid timezone issues
 *
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * formatDateToApiFormat(new Date('2025-11-26T00:00:00.000Z'))
 * // "2025-11-26"
 */
export const formatDateToNonISOFormat = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Normalizes a date to UTC midnight to avoid timezone issues
 */
export const normalizeNullableToStartOfDayUTC = (
  date: Date | null | undefined
): Date | null | undefined => {
  if (date === null) return null;
  if (date === undefined) return undefined;
  return normalizeToStartOfDayUTC(date);
};

/**
 * Returns today's date normalized to UTC midnight as an ISO string.
 * Use this when sending "today" as a revisionDate or similar calendar date to the API.
 */
export const getTodayISOString = (): string => normalizeToStartOfDayUTC(new Date()).toISOString();

/**
 * Normalizes a date to UTC midnight to avoid timezone issues
 */
export const normalizeToStartOfDayUTC = (date: Date): Date => {
  // Use local date components (year/month/day) to build UTC midnight.
  // e.g. user in UTC+1 selects Mar 16 → local getFullYear/Month/Date = 2026/2/16
  // → Date.UTC(2026, 2, 16) = "2026-03-16T00:00:00.000Z" ✓
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

/**
 * Parses a wall-clock ISO timestamp (backend `...Z` value) into a LOCAL Date whose
 * local Y/M/D/H/m equal the literal UTC components of the source — i.e. the digits
 * are preserved, NOT shifted into the user's timezone.
 *
 * Worklog timestamps are wall-clock values; the time-entry form holds them as local
 * Dates (the date picker reads local getters) and re-encodes them as UTC wall-clock on
 * save via {@link normalizeToWallTimeUTC}. Using `parseISO` directly would feed the form
 * a TZ-shifted day/time. This helper closes the round-trip so an unchanged edit is a no-op.
 *
 * @example
 * // browser at UTC+3
 * parseWallClockToLocalDate('2026-06-15T11:49:00.000Z') // local Date for 2026-06-15 11:49
 */
export const parseWallClockToLocalDate = (date: string): Date => {
  const utc = parseISO(date);
  return new Date(
    utc.getUTCFullYear(),
    utc.getUTCMonth(),
    utc.getUTCDate(),
    utc.getUTCHours(),
    utc.getUTCMinutes(),
    utc.getUTCSeconds()
  );
};

/**
 * Normalizes a date to a wall-clock UTC instant by reading local hour/minute components.
 * Use when the backend stores times as wall-clock (Z-suffixed but not truly UTC).
 * e.g. user in UTC+2 picks 10:10 → local getHours/Minutes = 10/10
 * → Date.UTC(..., 10, 10) = "2026-05-12T10:10:00.000Z" ✓ (digits match wall time)
 */
export const normalizeToWallTimeUTC = (date: Date): Date => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    )
  );
};

/**
 * Normalizes a date to UTC end of day to avoid timezone issues.
 * Uses local date components (year/month/day) to build UTC 23:59:59.999,
 * mirroring normalizeToStartOfDayUTC so both always refer to the same calendar day.
 */
export const normalizeToEndOfDayUTC = (date: Date): Date => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
};

/**
 * Formats date to YYYY-MM-DD format using LOCAL timezone
 * Useful for comparing dates where timezone should be ignored
 *
 * @param date - Date object or ISO string
 * @returns Date string in YYYY-MM-DD format (local timezone)
 *
 * @example
 * formatDateToLocalFormat(new Date('2025-11-26T00:00:00.000Z')) // "2025-11-26" (in local timezone)
 * formatDateToLocalFormat('2025-10-15T22:00:00.000Z') // "2025-10-16" (if local is UTC+2)
 */
/**
 * Returns the browser's IANA timezone identifier (e.g. 'America/New_York', 'Europe/Kyiv')
 */
export const getBrowserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const formatDateToLocalFormat = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date to short display format without year (e.g., "Jan 15")
 * Useful for date ranges within the same year
 *
 * @param date - Date object or ISO string
 * @returns Formatted short date string
 *
 * @example
 * formatShortDate(new Date('2025-01-15')) // "Jan 15"
 */
export const formatShortDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, SHORT_DATE_FORMAT);
};

/**
 * Formats a wall-clock timestamp to the short date without year (e.g. "Jun 1") by reading
 * its UTC components literally, WITHOUT converting to the user's timezone.
 *
 * Mirrors {@link formatWallClockDate} for the year-less {@link SHORT_DATE_FORMAT} — use for
 * backend worklog / calendar-range `...Z` values that must render exactly as received.
 */
export const formatWallClockShortDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, UTC_TIMEZONE, SHORT_DATE_FORMAT);
};

/**
 * Formats a date range for compact display (e.g., in table headers)
 * Uses short format if both dates are in the same year
 *
 * @param startDate - Start date (Date object or ISO string)
 * @param endDate - Optional end date (Date object, ISO string, or null)
 * @returns Formatted compact date range string
 *
 * @example
 * formatCompactDateRange(new Date('2025-01-01'), new Date('2025-02-28'))
 * // "Jan 1 - Feb 28, 2025"
 *
 * formatCompactDateRange(new Date('2024-12-01'), new Date('2025-02-28'))
 * // "Dec 1, 2024 - Feb 28, 2025"
 */
export const formatCompactDateRange = (
  startDate: Date | string,
  endDate?: Date | string | null
): string => {
  if (!endDate) {
    return `${formatDisplayDate(startDate)} - ∞`;
  }

  const startObj = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const endObj = typeof endDate === 'string' ? parseISO(endDate) : endDate;

  const startYear = startObj.getFullYear();
  const endYear = endObj.getFullYear();

  if (startYear === endYear) {
    return `${formatShortDate(startObj)} - ${formatDisplayDate(endObj)}`;
  }

  return `${formatDisplayDate(startObj)} - ${formatDisplayDate(endObj)}`;
};

export const formatWeekdayDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, WEEKDAY_DATE_FORMAT);
};

/**
 * Formats a wall-clock timestamp to a weekday-with-date header (e.g. "Monday, 15 Jun")
 * by reading its UTC components literally, WITHOUT converting to the user's timezone.
 *
 * Used by the time-tracking day-group headers so the weekday matches the literal
 * worklog date shown on each entry. See {@link formatWallClockDate} for rationale.
 */
export const formatWallClockWeekdayDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, UTC_TIMEZONE, WEEKDAY_DATE_FORMAT);
};

/**
 * Formats a date as a long weekday header (e.g., "Friday. May 8, 2026").
 * Used by the time-tracking day-detail panel.
 */
export const formatLongWeekdayDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, LONG_WEEKDAY_DATE_FORMAT);
};

/**
 * Formats a date as a weekday plus short date (e.g., "Friday, May 8").
 * Used by time-tracking quick-add popovers and similar compact contexts.
 */
export const formatWeekdayWithShortDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, WEEKDAY_WITH_SHORT_DATE_FORMAT);
};

/** Formats a date to a short weekday abbreviation (e.g., "Mon"). */
export const formatShortWeekday = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, SHORT_WEEKDAY_FORMAT);
};

/** Formats a date to its day-of-month number (e.g., "8"). */
export const formatDayNumber = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, DAY_NUMBER_FORMAT);
};

/**
 * Formats a date with time for display (e.g., "15 Jan 2025, 14:30")
 *
 * @param date - Date object or ISO string
 * @returns Formatted date-time string
 *
 * @example
 * formatDateTime(new Date('2025-01-15T14:30:00')) // "15 Jan 2025, 14:30"
 * formatDateTime('2025-01-15T14:30:00Z') // "15 Jan 2025, 14:30"
 */
export const formatDateTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, DATE_TIME_FORMAT);
};

/**
 * Formats a date to relative time (e.g., "2h ago") for recent dates,
 * or short date format (e.g., "May 26") for older dates.
 * Useful for notifications, activity feeds, and timestamps.
 *
 * @param date - Date object or ISO string
 * @param options - Optional configuration
 * @param options.recentThresholdDays - Number of days to show relative time (default: 7)
 * @returns Formatted relative time or date string
 *
 * @example
 * formatRelativeTime(new Date()) // "0 seconds ago"
 * formatRelativeTime(twoHoursAgo) // "2 hours ago"
 * formatRelativeTime(lastWeek) // "May 26"
 * formatRelativeTime(lastYear) // "May 26, 2024"
 */
export const formatRelativeTime = (
  date: Date | string,
  options?: { recentThresholdDays?: number }
): string => {
  const { recentThresholdDays = 7 } = options ?? {};
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24));

  // Show relative time for recent notifications
  if (diffInDays < recentThresholdDays) {
    return formatDistanceToNowStrict(dateObj, { addSuffix: true });
  }

  // Show date format for older notifications
  if (isThisYear(dateObj)) {
    return formatShortDate(dateObj);
  }

  return formatDisplayDate(dateObj);
};

/**
 * Formats minutes into "Xh Ym" format string using i18n.
 *
 * @param totalMinutes - Total minutes (e.g., 515 for 8h 35m)
 * @returns Formatted string like "8h 35m" or "8h"
 * @example
 * formatMinutesToTimeString(515) // returns "8h 35m"
 * formatMinutesToTimeString(480) // returns "8h"
 */
export const formatMinutesToTimeString = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return i18n.t('common:timeFormat.hoursOnly', { hours });
  }
  return i18n.t('common:timeFormat.hoursAndMinutes', { hours, minutes });
};
